/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */

import { IconChevronLeft } from "./icons/chevron-left.js";
import { IconChevronRight } from "./icons/chevron-right.js";

const styles = css`
  :host {
    display: block;
  }

  nav {
    align-items: center;
    display: flex;
    gap: 4px;
    justify-content: center;
  }

  button {
    align-items: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: var(--foreground, #18181b);
    cursor: pointer;
    display: inline-flex;
    font: inherit;
    font-size: 0.875rem;
    gap: 4px;
    height: 32px;
    justify-content: center;
    min-width: 32px;
    outline: none;
    padding: 0 8px;
    transition:
      background-color 140ms ease,
      border-color 140ms ease,
      box-shadow 140ms ease;
  }

  button:hover:not(:disabled) {
    background: var(--muted, #f4f4f5);
  }

  button:focus-visible {
    border-color: var(--ring, #18181b);
    box-shadow: 0 0 0 3px var(--ring-shadow, rgb(24 24 27 / 12%));
  }

  button[aria-current="page"] {
    border-color: var(--border, #e4e4e7);
    font-weight: 600;
  }

  button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .icon {
    height: 16px;
    width: 16px;
  }

  .ellipsis {
    align-items: center;
    color: var(--muted-foreground, #71717a);
    display: inline-flex;
    justify-content: center;
    min-width: 32px;
  }
`;

function pageList(total: number, current: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  const pages: (number | "...")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("...");
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < total - 1) pages.push("...");
  pages.push(total);
  return pages;
}

function emitPageChange(host: HTMLElement, totalAttr: string, pageAttr: string, request: string): void {
  const totalPages = Math.max(1, Number(totalAttr) || 1);
  const current = Math.min(totalPages, Math.max(1, Number(pageAttr) || 1));
  const target = request === "prev" ? current - 1 : request === "next" ? current + 1 : Number(request);
  if (target < 1 || target > totalPages || target === current) return;
  host.dispatchEvent(
    new CustomEvent("page-change", { bubbles: true, composed: true, detail: { page: target } }),
  );
}

export class AppPagination extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppPagination)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["total","data-total","page","data-page"];

  #total = "1";
  #page = "1";
  #totalSet = false;
  #pageSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #attr0;
  #attr1;
  #attrLast0;
  #attrLast1;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get total() {
    return this.#total;
  }

  set total(value) {
    const raw = value ?? "1";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#total, next)) return;
    this.#total = next;
    this.#totalSet = true;
    this.#updateBindings('total');
  }

  get page() {
    return this.#page;
  }

  set page(value) {
    const raw = value ?? "1";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#page, next)) return;
    this.#page = next;
    this.#pageSet = true;
    this.#updateBindings('page');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-total')) this.total = this.dataset.total;
    if (this.hasAttribute('data-page')) this.page = this.dataset.page;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.total = props.total;
      this.page = props.page;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#totalSet) this.total = this.dataset.total;
    if (!this.#pageSet) this.page = this.dataset.page;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'total':
      case 'data-total':
        this.total = newValue;
        break;
      case 'page':
      case 'data-page':
        this.page = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const totalPages = Math.max(1, Number(this.#total) || 1);
    const current = Math.min(totalPages, Math.max(1, Number(this.#page) || 1));
    const pageButtons = pageList(totalPages, current)
    .map((entry) => (entry === "..."
    ? '<span class="ellipsis">&hellip;</span>'
    : `<button type="button" data-page="${entry}" aria-current="${entry === current ? "page" : "false"}">${entry}</button>`))
    .join("");
    return `<style>${this.#escText(styles)}</style><nav aria-label="Pagination" data-argon="3251"><button type="button" class="prev" aria-label="Previous page"${(current === 1) ? ' disabled' : ''} data-argon="3635" data-argon-bind="0"><span class="icon"><icon-chevron-left><!--argon-text:0-->${""}<!--/argon-text:0--></icon-chevron-left></span></button><!--argon-text:1-->${pageButtons}<!--/argon-text:1--><button type="button" class="next" aria-label="Next page"${(current === totalPages) ? ' disabled' : ''} data-argon="3985" data-argon-bind="1"><span class="icon"><icon-chevron-right><!--argon-text:2-->${""}<!--/argon-text:2--></icon-chevron-right></span></button></nav>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const totalPages = Math.max(1, Number(this.#total) || 1);
    const current = Math.min(totalPages, Math.max(1, Number(this.#page) || 1));
    const pageButtons = pageList(totalPages, current)
    .map((entry) => (entry === "..."
    ? '<span class="ellipsis">&hellip;</span>'
    : `<button type="button" data-page="${entry}" aria-current="${entry === current ? "page" : "false"}">${entry}</button>`))
    .join("");
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'total' || prop === 'page') {
      const __argonNext = `${pageButtons}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'total' || prop === 'page') && this.#attr0) {
      this.#attr0.toggleAttribute('disabled', !!(current === 1));
    }
    if ((prop === undefined || prop === 'total' || prop === 'page') && this.#attr1) {
      this.#attr1.toggleAttribute('disabled', !!(current === totalPages));
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '3251').forEach(el => this.#listen(el, '3251', 'click', (event: Event) => {
    const target = (event.target as Element).closest("[data-page]");
    if (!target)
        return;
    emitPageChange(this, this.#total, this.#page, target.getAttribute("data-page") ?? "");
}));
    this.#eventTargets(scope, '3635').forEach(el => this.#listen(el, '3635', 'click', () => emitPageChange(this, this.#total, this.#page, "prev")));
    this.#eventTargets(scope, '3985').forEach(el => this.#listen(el, '3985', 'click', () => emitPageChange(this, this.#total, this.#page, "next")));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
